import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { loadRazorpay, openRazorpay } from "../../lib/razorpay";
import { useCart } from "../../contexts/CartContext";
import { useAuth } from "../../contexts/AuthContext";
import CustomerHeader from "../../components/CustomerHeader";
import { PhoneCapture } from "../../components/PhoneCapture";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Check, MapPin, Receipt, Wallet, ArrowLeft, CreditCard, Clock, Shield, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "address", label: "Address" },
  { key: "summary", label: "Summary" },
  { key: "payment", label: "Payment" },
];

export default function Checkout() {
  const navigate = useNavigate();
  const cart = useCart();
  const { user } = useAuth();
  const [step, setStep] = useState("address");
  const [addresses, setAddresses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newAddr, setNewAddr] = useState({ label: "Home", line1: "", city: "Bengaluru", pincode: "" });
  const [payment, setPayment] = useState("cod");
  const [placing, setPlacing] = useState(false);
  const placedRef = useRef(false);

  useEffect(() => {
    if (!user) return navigate("/login?role=customer&next=/checkout");
    if (cart.itemsArray.length === 0) {
      if (!placedRef.current) navigate("/app");
      return;
    }
    api.get("/me/addresses").then((r) => {
      setAddresses(r.data);
      if (r.data?.length) setSelected(r.data[0].id);
    }).catch(() => {});
  }, [user, cart.itemsArray.length, navigate]);

  const saveAddress = async () => {
    if (!newAddr.line1 || newAddr.line1.length < 6) { toast.error("Enter a full address"); return; }
    try {
      const { data } = await api.post("/me/addresses", newAddr);
      setAddresses((prev) => [...prev, data]);
      setSelected(data.id);
      setNewAddr({ label: "Home", line1: "", city: "Bengaluru", pincode: "" });
      toast.success("Address saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const pinLocation = () => {
    if (!navigator.geolocation) return toast.error("Location isn't supported on this device");
    navigator.geolocation.getCurrentPosition(
      (p) => { setNewAddr((a) => ({ ...a, lat: p.coords.latitude, lng: p.coords.longitude })); toast.success("Location pinned"); },
      () => toast.error("Couldn't get your location. Allow location access and try again."),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const finishOrder = (orderId, msg) => {
    placedRef.current = true;
    cart.clear();
    toast.success(msg);
    navigate(`/orders/${orderId}`);
  };

  const payOnline = async (order) => {
    const resp = await openRazorpay({ rz: order.razorpay, user, shopName: order.shop_name });
    if (!resp) {
      const { data: c } = await api.post(`/payments/razorpay/cancel/${order.order_id}`).catch((e) => {
        console.error("Cancel unpaid order failed:", e);
        return { data: {} };
      });
      if (c.paid) return finishOrder(order.order_id, "Payment received. Order placed");
      toast.error("Payment cancelled. Your cart is still here.");
      return;
    }
    await api.post("/payments/razorpay/verify", { order_id: order.order_id, ...resp });
    finishOrder(order.order_id, "Payment successful. Order placed");
  };

  const placeOrder = async () => {
    const addr = addresses.find((a) => a.id === selected);
    if (!addr) return toast.error("Pick an address");
    setPlacing(true);
    try {
      if (payment === "razorpay" && !(await loadRazorpay())) {
        toast.error("Couldn't load the payment window. Check your connection.");
        return;
      }
      const items = cart.itemsArray.map((it) => ({ product_id: it.product_id, qty: it.qty }));
      const addrText = `${addr.label}: ${addr.line1}${addr.city ? ", " + addr.city : ""}${addr.pincode ? " - " + addr.pincode : ""}`;
      const { data } = await api.post("/orders", {
        shop_id: cart.cart.shop.shop_id,
        items,
        address: addrText,
        payment_method: payment,
        tip: 0,
        drop_lat: addr.lat ?? null,
        drop_lng: addr.lng ?? null,
      });
      if (payment === "razorpay") await payOnline(data);
      else finishOrder(data.order_id, "Order placed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  const stepIdx = STEPS.findIndex((s) => s.key === step);

  return (
    <div className="light-scope min-h-screen bg-slate-50" data-testid="checkout-container">
      <CustomerHeader onCartClick={() => {}} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate("/app")} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4" data-testid="back-shop-btn">
          <ArrowLeft size={14} /> Continue shopping
        </button>

        {/* Stepper */}
        <div className="rounded-2xl bg-white border border-slate-200 p-4 flex items-center justify-between mb-4">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className="flex items-center gap-2" data-testid={`checkout-step-${s.key}`}>
                <div className={`w-8 h-8 rounded-full inline-flex items-center justify-center text-sm font-bold ${i <= stepIdx ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                  {i < stepIdx ? <Check size={14} /> : i + 1}
                </div>
                <div className={`font-semibold text-sm ${i <= stepIdx ? "text-slate-900" : "text-slate-500"}`}>{s.label}</div>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 mx-3 h-1 rounded-full ${i < stepIdx ? "bg-emerald-500" : "bg-slate-200"}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-4">
            {step === "address" && (
              <div className="rounded-2xl bg-white border border-slate-200 p-5">
                {!user?.phone && <PhoneCapture />}
                <h2 className="font-extrabold text-lg flex items-center gap-2 text-slate-900"><MapPin size={16} className="text-emerald-600" /> Delivery address</h2>

                {addresses.length > 0 && (
                  <RadioGroup value={selected || ""} onValueChange={setSelected} className="mt-4 space-y-2">
                    {addresses.map((a) => (
                      <label
                        key={a.id}
                        htmlFor={`addr-${a.id}`}
                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${selected === a.id ? "border-emerald-500 bg-emerald-50/50" : "border-slate-200 hover:border-slate-400"}`}
                        data-testid={`address-option-${a.id}`}
                      >
                        <RadioGroupItem value={a.id} id={`addr-${a.id}`} className="mt-0.5" />
                        <div>
                          <div className="font-bold text-slate-900">{a.label}</div>
                          <div className="text-sm text-slate-600">{a.line1}{a.city ? `, ${a.city}` : ""}{a.pincode ? ` - ${a.pincode}` : ""}</div>
                        </div>
                      </label>
                    ))}
                  </RadioGroup>
                )}

                <div className="mt-5 border-t border-slate-200 pt-4">
                  <div className="font-bold text-slate-900 mb-3">Add a new address</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500 tracking-widest">Label</Label>
                      <Input value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} placeholder="Home / Work" data-testid="addr-label-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500 tracking-widest">Pincode</Label>
                      <Input value={newAddr.pincode} onChange={(e) => setNewAddr({ ...newAddr, pincode: e.target.value })} placeholder="560038" data-testid="addr-pin-input" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-bold uppercase text-slate-500 tracking-widest">Address</Label>
                      <Textarea rows={2} value={newAddr.line1} onChange={(e) => setNewAddr({ ...newAddr, line1: e.target.value })} placeholder="Flat 302, Diamond Residency, 100-ft Road, Indiranagar" data-testid="addr-line1-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500 tracking-widest">City</Label>
                      <Input value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} data-testid="addr-city-input" />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button type="button" variant="outline" onClick={pinLocation} data-testid="addr-pin-location-btn">
                      {newAddr.lat ? "Location pinned" : "Pin my current location"}
                    </Button>
                    <Button onClick={saveAddress} className="bg-slate-900 hover:bg-slate-800" data-testid="save-addr-btn">Save address</Button>
                  </div>
                </div>
              </div>
            )}

            {step === "summary" && (
              <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="summary-panel">
                <h2 className="font-extrabold text-lg flex items-center gap-2 text-slate-900"><Receipt size={16} className="text-emerald-600" /> Order summary</h2>
                <div className="mt-4 space-y-2">
                  {cart.itemsArray.map((it) => (
                    <div key={it.product_id} className="flex items-center gap-3">
                      <img src={it.image_url} alt="" className="w-12 h-12 rounded-lg object-cover border border-slate-100" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{it.name}</div>
                        <div className="text-[11px] text-slate-500">{it.unit} · × {it.qty}</div>
                      </div>
                      <div className="font-bold text-sm">₹{(it.price * it.qty).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-sm text-emerald-800 flex items-center gap-2">
                  <Clock size={14} /> Delivery in ~{cart.cart.shop?.eta_minutes || 10} minutes from <b>{cart.cart.shop?.name}</b>
                </div>
              </div>
            )}

            {step === "payment" && (
              <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="payment-panel">
                <h2 className="font-extrabold text-lg flex items-center gap-2 text-slate-900"><Wallet size={16} className="text-emerald-600" /> Payment method</h2>
                <RadioGroup value={payment} onValueChange={setPayment} className="mt-4 space-y-2">
                  <PayOption id="cod" selected={payment === "cod"} icon={Wallet} title="Cash on Delivery" subtitle="Pay the rider when it arrives" />
                  <PayOption id="razorpay" selected={payment === "razorpay"} icon={CreditCard} title="UPI / Cards / Netbanking" subtitle="Pay securely with Razorpay" />
                </RadioGroup>
                <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                  <Shield size={12} /> Your details are protected with industry-grade encryption.
                </div>
              </div>
            )}

            {/* Nav */}
            <div className="flex items-center justify-between pt-2">
              {stepIdx > 0 ? (
                <Button variant="outline" onClick={() => setStep(STEPS[stepIdx - 1].key)} data-testid="checkout-back-btn">Back</Button>
              ) : <div />}
              {stepIdx < STEPS.length - 1 ? (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                  disabled={step === "address" && (!selected || !user?.phone)}
                  onClick={() => setStep(STEPS[stepIdx + 1].key)}
                  data-testid="checkout-next-btn"
                >
                  Continue <ChevronRight size={16} className="ml-1" />
                </Button>
              ) : (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                  disabled={placing}
                  onClick={placeOrder}
                  data-testid="place-order-btn"
                >
                  {placing ? (payment === "razorpay" ? "Opening payment…" : "Placing…") : `${payment === "razorpay" ? "Pay" : "Place order"} · ₹${cart.total.toFixed(2)}`}
                </Button>
              )}
            </div>
          </div>

          <aside>
            <div className="rounded-2xl bg-white border border-slate-200 p-5 sticky top-28">
              <div className="font-extrabold text-slate-900">Bill details</div>
              <div className="mt-3 space-y-1.5 text-sm">
                <Line label={`Items (${cart.count})`} value={`₹${cart.subtotal.toFixed(2)}`} />
                <Line label="Delivery fee" value={cart.deliveryFee ? `₹${cart.deliveryFee}` : "FREE"} good={!cart.deliveryFee} />
                <div className="h-px bg-slate-200 my-2" />
                <Line label="To pay" value={`₹${cart.total.toFixed(2)}`} big />
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

function Line({ label, value, big, good }) {
  return (
    <div className="flex items-center justify-between">
      <span className={big ? "font-bold text-slate-900" : "text-slate-600"}>{label}</span>
      <span className={big ? "font-extrabold text-emerald-700 text-lg" : good ? "text-emerald-600 font-semibold" : "font-semibold text-slate-900"}>{value}</span>
    </div>
  );
}

function PayOption({ id, title, subtitle, icon: Icon, selected, disabled }) {
  return (
    <label htmlFor={`pay-${id}`} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${disabled ? "opacity-50 pointer-events-none" : ""} ${selected ? "border-emerald-500 bg-emerald-50/50" : "border-slate-200 hover:border-slate-400"}`}
      data-testid={`payment-${id}`}>
      <RadioGroupItem value={id} id={`pay-${id}`} className="mt-1" disabled={disabled} />
      <Icon size={18} className="text-slate-700 mt-0.5" />
      <div>
        <div className="font-bold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500">{subtitle}</div>
      </div>
    </label>
  );
}
